import { Fragment } from 'react';
import BootstrapTable from 'react-bootstrap/Table';
import TablePagination from '@/components/TablePagination/TablePagination';
import Button from '@/components/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classes from './Table.module.scss';
import t from '@/utils/Language/UI-translations';

/**
 * Table UI
 * @param className   string      className to apply to the table
 * @param headerClassName   string      className to apply to the table header
 * @param content     array       Content to populate the table
 * @param columns     array       Column objects identifying the tables column settings
 * {
 *    key:            string      unique column identifier for the able
 *    title:          string      header for the column
 *    hideOnViewport: string      viewport context key when to hide column
 *    sortable:       boolean     whether column supports sorting
 * }
 * @param onPageClick function    callback when new page is clicked
 * @param onSort      function    callback when table is sorted
 * @param onViewMore  function    callback when view more button is clicked (and when set displays view more)
 * @param pagination  object      a pagination object returned with the ODAS/ODRS API response
 * @param responsive  sm|md|lg|xl the Bootstrap breakpoint to make the table horizontally scrollable
 * @param showHeader  boolean     allows header row to be toggled as visible or hidden
 * @param size        sm|null     setting to "sm" will decrease the padding/spacing inside the table
 * @param sortColumn  string      key of the sorted column
 * @param sortOrder   asc|desc    order of the current sort
 * @param props       object      Other properties
 * @param onPageChange function    callback when page change
 */
function Table({
  className = '',
  content = [],
  columns = [],
  onPageClick,
  onSort,
  withTabs,
  onViewMore = null,
  pagination = null,
  responsive = 'sm',
  showHeader = true,
  size = null,
  sortColumn,
  sortOrder = 'asc',
  onPageChange,
  expandedSubrowsOne,
  expandedSubrowsTwo,
  ...props
}) {
  const addSortIcon = () => {
    if (sortOrder === 'asc')
      return (
        <span className={classes.up}>
          <FontAwesomeIcon icon={['fas', 'sort-up']} />
        </span>
      );
    else if (sortOrder === 'desc')
      return (
        <span className={classes.down}>
          <FontAwesomeIcon icon={['fas', 'sort-down']} />
        </span>
      );
    return null;
  };

  const tableClasses = [classes.baseTable, className, classes[withTabs ? 'withTabs' : '']].join(' ');

  return (
    <>
      <BootstrapTable className={`${tableClasses}`} hover size={size} responsive={responsive}>
        {showHeader && (
          <thead>
            <tr>
              {columns.map((column, index) => {
                if (column.hidden) {
                  return <span key={index}></span>;
                }
                return (
                  <th key={index} onClick={column.sortable && typeof onSort === 'function' ? () => onSort(column.key, sortOrder) : null}>
                    <div
                      data-cy={`table-sort-${column.title.replace(/\s+/g, '-').toLowerCase()}`}
                      key={index}
                      className={
                        (column.key == sortColumn ? classes.activeSort : '') +
                        ' ' +
                        (column.sortable ? classes.sortable : '') +
                        ' ' +
                        (column.headerClassName ? column.headerClassName : '')
                      }
                    >
                      {t(column.title)}
                      {column.key == sortColumn ? addSortIcon() : null}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
        )}
        <tbody>
          {content.map((row, index) => {
            return (
              <Fragment key={index}>
                <tr data-cy={`table-row-index-${index}`} key={index}>
                  {columns.map((column, index) => {
                    return (
                      <td
                        data-cy={`table-td-${column.title.replace(/\s+/g, '-').toLowerCase()}`}
                        key={index}
                        className={column.className ? column.className : ''}
                      >
                        {row[column.key]}
                      </td>
                    );
                  })}
                </tr>
                {expandedSubrowsOne && expandedSubrowsOne.length > 0 && expandedSubrowsOne.includes(row.id) && (
                  <tr className={classes.subrow}>
                    <td colSpan={columns.length} className={classes.leftBorder}>
                      <div className={classes.subrowContent}>{row.subrowContentOne}</div>
                    </td>
                  </tr>
                )}
                {expandedSubrowsTwo && expandedSubrowsTwo.length > 0 && expandedSubrowsTwo.includes(row.id) && (
                  <tr className={classes.subrow}>
                    <td colSpan={columns.length} className={classes.leftBorder}>
                      <div className={classes.subrowContent}>{row.subrowContentTwo}</div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {!pagination?.total_pages && onViewMore && typeof onViewMore === 'function' && (
            <tr>
              <td colSpan={columns.length} className="text-center">
                <Button type="link" text={t('View more')} onButtonClick={onViewMore} />
              </td>
            </tr>
          )}
        </tbody>
      </BootstrapTable>
      {pagination?.total_pages && <TablePagination pagination={pagination} onPageChange={onPageChange} />}
    </>
  );
}

export default Table;
